import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import {
  Menu, X, ArrowUpRight, Mail, Phone, ChevronDown,
  Sparkles, Layout, Waves, Lock, Repeat2, Database, Globe, Server, Rocket,
  Code2, Megaphone, FileSpreadsheet, Landmark, ShieldAlert, Users, Star,
} from "lucide-react";
import {
  BRAND,
  NAV_ITEMS,
  NAV_ROUTES,
  FX_DROPDOWN,
  FUNDED_FORM_DROPDOWN,
  SERVICES_DROPDOWN,
  navItemPath,
  dropdownItemPath,
} from "../lib/constants";
import ThemeToggle from "./ThemeToggle";

const ICON_MAP = {
  Sparkles, Layout, Waves, Lock, Repeat2, Database, Globe, Server, Rocket,
  Code2, Megaphone, FileSpreadsheet, Landmark, ShieldAlert, Users, UserStar: Star,
};

const DROPDOWN_BY_ID = {
  fx: FX_DROPDOWN,
  funded: FUNDED_FORM_DROPDOWN,
  services: SERVICES_DROPDOWN,
};

function NavDropdownPanel({ columns, onNavigate }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.15 }}
      className="w-full border-t theme-dropdown backdrop-blur-xl"
    >
      <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {columns.map((col) => (
          <div key={col.group}>
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#9FE3FF] mb-4 pb-2 border-b border-white/10">
              {col.group}
            </div>
            <ul className="space-y-1">
              {col.items.map((item) => {
                const Icon = ICON_MAP[item.icon] || Sparkles;
                const to = dropdownItemPath(item);
                return (
                  <li key={item.title}>
                    <Link
                      to={to}
                      onClick={onNavigate}
                      className="group flex gap-3 py-2.5 px-2 rounded-lg hover:bg-white/[0.04] transition-colors"
                    >
                      <div className="w-9 h-9 rounded-md bg-[#00C2FF]/10 border border-[#00C2FF]/25 flex items-center justify-center shrink-0">
                        <Icon className="w-4 h-4 text-[#00C2FF]" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold theme-heading group-hover:text-[#00C2FF]">
                          {item.title}
                        </div>
                        <div className="text-[11px] text-gray-500 mt-0.5 line-clamp-2 leading-snug">
                          {item.desc}
                        </div>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [expandedOverlay, setExpandedOverlay] = useState(null);
  const location = useLocation();

  const activeColumns = activeDropdown ? DROPDOWN_BY_ID[activeDropdown] : null;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    setOpen(false);
    setActiveDropdown(null);
    setExpandedOverlay(null);
  }, [location.pathname, location.hash]);

  const isActiveNav = (item) => {
    const to = navItemPath(item);
    if (to === location.pathname) return true;
    if (to.startsWith("/#") && location.pathname === "/" && location.hash === to.slice(1)) return true;
    if (location.pathname.startsWith("/solutions") && (item.dropdownId === "fx" || item.dropdownId === "funded")) return true;
    if (location.pathname.startsWith("/services") && item.dropdownId === "services") return true;
    if (location.pathname === "/web-tech" && item.dropdownId === "fx") return true;
    return false;
  };

  const closeMenus = () => {
    setOpen(false);
    setActiveDropdown(null);
    setExpandedOverlay(null);
  };

  return (
    <>
      <header data-testid="site-header" className="fixed top-0 left-0 right-0 z-50">
        {/* Top strip */}
        <div className="hidden lg:block theme-header-top">
          <div className="max-w-7xl mx-auto px-6 py-2 flex justify-between items-center text-[11px] theme-muted">
            <div className="flex items-center gap-6">
              <a href={`mailto:${BRAND.email}`} className="hover:text-[#00C2FF] transition-colors flex items-center gap-2">
                <Mail className="w-3 h-3" /> {BRAND.email}
              </a>
              <a href={`tel:${BRAND.phone.replace(/\s/g, "")}`} className="hover:text-[#9FE3FF] transition-colors flex items-center gap-2">
                <Phone className="w-3 h-3" /> {BRAND.phone}
              </a>
            </div>
            <span className="font-mono uppercase tracking-[0.35em] text-[#00C2FF]/80">{BRAND.shortName}</span>
          </div>
        </div>

        {/* Main bar */}
        <div
          className={`border-b transition-colors ${
            scrolled ? "theme-header-bar-scrolled" : "theme-header-bar"
          }`}
          onMouseLeave={() => setActiveDropdown(null)}
        >
          <div className="max-w-7xl mx-auto px-6 flex items-center py-3 sm:py-4 gap-6">
            <Link to="/" data-testid="logo-link" className="flex items-center shrink-0 max-w-[200px]">
              <img
                src={BRAND.logo}
                alt={BRAND.name}
                className="h-14 sm:h-20 w-auto max-w-[200px] object-contain object-left"
              />
            </Link>

            <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center">
              {NAV_ITEMS.map((item) => {
                const to = navItemPath(item);
                const isDropdown = item.hasDropdown && item.dropdownId;
                const active = isActiveNav(item) || activeDropdown === item.dropdownId;
                return (
                  <div
                    key={item.label}
                    onMouseEnter={() => isDropdown && setActiveDropdown(item.dropdownId)}
                  >
                    <Link
                      to={to}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                      className={`nav-underline flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                        active ? "nav-underline-active theme-nav-link-active" : "theme-nav-link"
                      }`}
                    >
                      {item.label}
                      {isDropdown && (
                        <ChevronDown className={`w-3.5 h-3.5 opacity-50 ${activeDropdown === item.dropdownId ? "rotate-180" : ""}`} />
                      )}
                    </Link>
                  </div>
                );
              })}
            </nav>

            <div className="flex items-center gap-3 ml-auto shrink-0">
              <ThemeToggle className="hidden sm:inline-flex" />
              <Link
                to="/contact"
                data-testid="header-cta"
                className="hidden sm:inline-flex items-center gap-2 bg-[#00C2FF] hover:bg-[#4DD9FF] text-[#000000] px-5 py-2 rounded-md text-sm font-bold transition-colors"
              >
                Request demo
                <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
              <button
                onClick={() => setOpen(true)}
                data-testid="menu-trigger"
                aria-label="Open menu"
                className="lg:hidden p-2.5 rounded-md border theme-panel theme-heading hover:border-[#00C2FF]/50"
              >
                <Menu className="w-5 h-5" />
              </button>
            </div>
          </div>

          <AnimatePresence>
            {activeColumns && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="hidden lg:block overflow-hidden"
                onMouseEnter={() => setActiveDropdown(activeDropdown)}
              >
                <NavDropdownPanel columns={activeColumns} onNavigate={closeMenus} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] lg:hidden"
          >
            <div className="absolute inset-0 theme-overlay" onClick={closeMenus} />
            <motion.aside
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 320 }}
              data-testid="overlay-menu"
              className="absolute right-0 top-0 bottom-0 w-[min(100%,340px)] theme-panel border-l flex flex-col"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                <span className="font-display font-bold theme-heading">{BRAND.shortName}</span>
                <div className="flex items-center gap-2">
                  <ThemeToggle />
                <button onClick={closeMenus} data-testid="menu-close" aria-label="Close menu" className="p-2 theme-muted hover:text-[#00C2FF]">
                <X className="w-5 h-5" />
              </button>
                </div>
            </div>

              <nav className="flex-1 overflow-y-auto px-5 py-6">
                <ul className="space-y-1">
                  {NAV_ROUTES.map((r, i) => {
                    const item = NAV_ITEMS[i];
                    const columns = item?.dropdownId ? DROPDOWN_BY_ID[item.dropdownId] : null;
                    const isExpanded = expandedOverlay === item?.dropdownId;
                    return (
                      <li key={r.label}>
                        <div className="flex items-center">
                          <Link
                            to={r.path}
                            onClick={closeMenus}
                            data-testid={`overlay-nav-${r.label.toLowerCase().replace(/\s/g, "-")}`}
                            className="flex-1 py-3 text-lg font-semibold theme-drawer-title"
                          >
                            {r.label}
                          </Link>
                          {columns && (
                            <button
                              type="button"
                              onClick={() => setExpandedOverlay(isExpanded ? null : item.dropdownId)}
                              className="p-3 text-gray-500"
                              aria-expanded={isExpanded}
                            >
                              <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                            </button>
                          )}
                        </div>
                        {columns && isExpanded && (
                          <ul className="pb-4 pl-3 border-l border-[#00C2FF]/30 space-y-3">
                            {columns.flatMap((col) => col.items).map((sub) => (
                              <li key={sub.title}>
                                <Link
                                  to={dropdownItemPath(sub)}
                                  onClick={closeMenus}
                                  className="text-sm text-gray-400 hover:text-[#9FE3FF]"
                                >
                                  {sub.title}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </nav>

              <div className="px-5 py-6 border-t border-white/10 space-y-3 text-xs text-gray-500">
                <a href={`mailto:${BRAND.email}`} className="flex items-center gap-2 hover:text-white">
                  <Mail className="w-3.5 h-3.5 text-[#00C2FF]" /> {BRAND.email}
                </a>
                <a
                  href={`tel:${BRAND.phone.replace(/[\s()-]/g, "")}`}
                  className="flex items-center gap-2 hover:text-white"
                >
                  <Phone className="w-3.5 h-3.5 text-[#9FE3FF]" /> {BRAND.phone}
                </a>
              </div>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
